import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { fetchIntercomMessengerToken } from '../../services/intercom.service';
import { setIntercomTokenAction } from '../../redux/actions/user';
import { IIntercomSession } from '../../interfaces/Intercom';
import { shouldHideIntercomLauncher } from '../../utils/intercomLauncher';

const EXPIRY_BUFFER_SECONDS = 5 * 60; // refresh if less than 5 min remaining

const getIntercomAppId = () => import.meta.env.REACT_APP_INTERCOM_APP_ID as string | undefined;

const isIntercomEnabled = () =>
  import.meta.env.REACT_APP_INTERCOM_ENABLED === 'true' && !!getIntercomAppId();

const isTokenExpired = (expiresAt: number | undefined): boolean => {
  if (!expiresAt) return true;
  return expiresAt - Math.floor(Date.now() / 1000) < EXPIRY_BUFFER_SECONDS;
};

export function useIntercom() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user: currentUser, intercom } = useSelector((state: any) => state.user);
  const isBootedRef = useRef(false);
  const lastBootedUserIdRef = useRef<string | null>(null);
  const lastLauncherHiddenRef = useRef<boolean | null>(null);

  useEffect(() => {
    const intercomAppId = getIntercomAppId();

    if (!isIntercomEnabled()) {
      if (isBootedRef.current) {
        shutdown();
        isBootedRef.current = false;
        lastLauncherHiddenRef.current = null;
      }
      return;
    }

    const userId = currentUser?._id;

    if (!userId) {
      if (isBootedRef.current) {
        shutdown();
        isBootedRef.current = false;
        lastLauncherHiddenRef.current = null;
      }
      return;
    }

    // Only bail if the backend explicitly set enabled: false
    if (intercom !== null && intercom?.enabled === false) {
      return;
    }

    const bootOrUpdate = async () => {
      let sessionData: IIntercomSession | null = intercom;

      // Fetch/refresh JWT if missing or about to expire
      if (!sessionData?.jwt || isTokenExpired(sessionData?.expiresAt)) {
        const fresh = await fetchIntercomMessengerToken(sessionData?.jwt);
        if (!fresh || !fresh.enabled) {
          return;
        }
        sessionData = fresh;
        dispatch(setIntercomTokenAction(fresh));
      }

      const hideDefaultLauncher = shouldHideIntercomLauncher(location.pathname);
      const payload = {
        app_id: intercomAppId!,
        user_id: userId,
        intercom_user_jwt: sessionData.jwt,
        name: currentUser.name,
        email: currentUser.email,
        hide_default_launcher: hideDefaultLauncher,
      };

      if (isBootedRef.current && lastBootedUserIdRef.current === userId) {
        if (lastLauncherHiddenRef.current !== hideDefaultLauncher) {
          update({ hide_default_launcher: hideDefaultLauncher });
          lastLauncherHiddenRef.current = hideDefaultLauncher;
        }
        // Same user already booted — skip boot/update unless launcher visibility changed.
        return;
      }

      if (isBootedRef.current) {
        update(payload);
        lastBootedUserIdRef.current = userId;
        lastLauncherHiddenRef.current = hideDefaultLauncher;
      } else {
        Intercom(payload);
        isBootedRef.current = true;
        lastBootedUserIdRef.current = userId;
        lastLauncherHiddenRef.current = hideDefaultLauncher;
      }
    };

    bootOrUpdate();
  }, [currentUser?._id, intercom, location.pathname]);
}
